import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, MessageSquare, Send, Video, Loader2, Sparkles, Mail } from 'lucide-react';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Human-friendly relative date formatter for the conversation list.
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'short' });
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const MessagesTab = ({ api, token }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const threadEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const res = await api().get('/admin/conversations');
      setConversations(res.data.conversations || []);
    } catch {
      toast.error('Failed to load conversations');
    }
    setLoading(false);
  };

  const sendReply = async () => {
    if (!replyContent.trim() || !selectedConversation) return;
    setSendingReply(true);
    try {
      // Find an existing conversation thread ID (any admin_messages doc from
      // this user). If none exists (brand-new thread — "0 messages"), fall
      // back to the /admin/messages endpoint which creates the initial doc.
      const existing = (selectedConversation.messages || []).find((m) => m.id && !m.id.toString().startsWith('tmp'));
      const conversationId = existing?.id;

      let newMessage;
      if (conversationId) {
        const formData = new FormData();
        formData.append('content', replyContent);
        const res = await fetch(`${API}/api/admin/messages/${conversationId}/reply`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
          body: formData,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } else {
        // No conversation yet — create a fresh one. The initial admin
        // message needs a subject; use a friendly opener since there's no
        // prior thread to "reply" to.
        const res = await api().post('/admin/messages', {
          user_id: selectedConversation.user_id,
          subject: 'A message from your coach',
          content: replyContent,
        });
        if (res.status >= 400) throw new Error(`HTTP ${res.status}`);
      }

      newMessage = {
        id: `tmp-${Date.now()}`,
        sender_type: 'admin',
        sender_name: 'Coach',
        content: replyContent,
        created_at: new Date().toISOString(),
      };
      setSelectedConversation((prev) => ({ ...prev, messages: [newMessage, ...(prev.messages || [])] }));

      setReplyContent('');
      toast.success('Reply sent!');
      fetchConversations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send reply');
    }
    setSendingReply(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Conversations list */}
        <div
          className="relative lg:col-span-1 rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-900/80 to-black/60 shadow-[0_8px_30px_rgba(0,0,0,0.35)] overflow-hidden"
          data-testid="conversations-list"
        >
          <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-primary/5 blur-3xl" />
          <div className="relative">
            <div className="p-4 border-b border-white/[0.06]">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary bg-primary/[0.08] border border-primary/25 rounded-full px-2.5 py-1 mb-2">
                <Sparkles className="h-3 w-3" /> Inbox
              </div>
              <h3 className="font-heading text-xl uppercase tracking-wider leading-tight bg-gradient-to-b from-white to-white/70 bg-clip-text text-transparent">
                Conversations
              </h3>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-primary bg-primary/[0.08] border border-primary/25 rounded-full px-2.5 py-0.5 mt-2">
                <MessageSquare className="h-3 w-3" /> {conversations.length} {conversations.length === 1 ? 'user' : 'users'}
              </span>
            </div>
            <div className="max-h-[600px] overflow-y-auto p-2">
              {conversations.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="h-14 w-14 rounded-full bg-primary/10 border border-primary/25 flex items-center justify-center mx-auto mb-3">
                    <MessageSquare className="h-6 w-6 text-primary" />
                  </div>
                  <p className="text-white/90 text-sm font-medium">No conversations yet</p>
                  <p className="text-muted-foreground text-xs mt-1">Athlete replies will appear here.</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {conversations.map((conv) => {
                    const isSelected = selectedConversation?.user_id === conv.user_id;
                    const initial = (conv.user_name || 'U').charAt(0).toUpperCase();
                    return (
                      <button
                        key={conv.user_id}
                        type="button"
                        onClick={() => {
                          setSelectedConversation(conv);
                          setTimeout(() => {
                            document.querySelector('[data-testid="conversation-detail"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }, 50);
                        }}
                        data-testid={`conv-${conv.user_id}`}
                        className={`w-full text-left rounded-xl px-3 py-2.5 transition-all group flex items-center gap-3 ${
                          isSelected
                            ? 'bg-primary/[0.1] border border-primary/40 shadow-[0_0_20px_rgba(250,204,21,0.08)]'
                            : 'bg-white/[0.02] border border-white/[0.06] hover:border-primary/25 hover:bg-white/[0.04]'
                        }`}
                      >
                        <span className={`h-10 w-10 rounded-full flex items-center justify-center font-heading text-sm flex-shrink-0 ${
                          isSelected ? 'bg-primary/20 text-primary border border-primary/40' : 'bg-white/[0.04] text-white/70 border border-white/10'
                        }`}>
                          {initial}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <p className={`text-sm font-medium truncate ${isSelected ? 'text-white' : 'text-white/90'}`}>{conv.user_name}</p>
                            <span className="text-[10px] text-muted-foreground flex-shrink-0">{formatDate(conv.last_activity)}</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground truncate mt-0.5">{conv.user_email}</p>
                          {conv.messages?.[0]?.content && (
                            <p className="text-xs text-white/50 truncate mt-1">{conv.messages[0].content.substring(0, 60)}</p>
                          )}
                        </div>
                        {conv.unread_replies > 0 && (
                          <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex-shrink-0">
                            {conv.unread_replies}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Conversation detail */}
        <div
          className="relative lg:col-span-2 rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-900/80 to-black/60 shadow-[0_8px_30px_rgba(0,0,0,0.35)] overflow-hidden"
          data-testid="conversation-detail"
        >
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-primary/8 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-48 w-48 rounded-full bg-primary/5 blur-3xl" />

          {selectedConversation ? (
            <div className="relative">
              <div className="p-4 border-b border-white/[0.06] flex items-center gap-3">
                <button
                  onClick={() => {
                    setSelectedConversation(null);
                    document.querySelector('[data-testid="conversations-list"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="lg:hidden h-9 w-9 rounded-full flex items-center justify-center text-white/70 hover:text-white bg-white/[0.04] border border-white/10 hover:border-primary/40 transition-all flex-shrink-0"
                  aria-label="Back to conversations"
                  data-testid="back-to-conversations"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <span className="hidden lg:flex h-11 w-11 rounded-full bg-primary/15 border border-primary/40 items-center justify-center font-heading text-primary flex-shrink-0">
                  {(selectedConversation.user_name || 'U').charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading text-base uppercase tracking-wider text-white truncate">{selectedConversation.user_name}</h3>
                  <p className="text-muted-foreground text-[11px] truncate flex items-center gap-1">
                    <Mail className="h-3 w-3 flex-shrink-0" /> {selectedConversation.user_email}
                  </p>
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary/[0.08] border border-primary/30 rounded-full px-2.5 py-1 flex-shrink-0">
                  {selectedConversation.messages?.length || 0} msgs
                </span>
              </div>

              <div className="h-[420px] overflow-y-auto p-4 space-y-3">
                {(selectedConversation.messages?.length || 0) === 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="h-12 w-12 rounded-full bg-primary/10 border border-primary/25 flex items-center justify-center mx-auto mb-3">
                        <MessageSquare className="h-5 w-5 text-primary" />
                      </div>
                      <p className="text-white/70 text-sm">No messages yet — start the conversation below.</p>
                    </div>
                  </div>
                ) : (
                  [...(selectedConversation.messages || [])].reverse().map((msg, i) => {
                    const isAdmin = msg.sender_type === 'admin';
                    return (
                      <div key={msg.id || i} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-2xl p-3 border ${
                          isAdmin
                            ? 'bg-primary/[0.12] border-primary/40 text-white'
                            : 'bg-sky-500/[0.1] border-sky-400/30 text-white'
                        }`}>
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className={`text-xs font-semibold ${isAdmin ? 'text-primary' : 'text-sky-300'}`}>
                              {isAdmin ? 'You' : selectedConversation.user_name}
                            </span>
                            <span className="text-muted-foreground text-[10px]">{formatDate(msg.created_at)}</span>
                          </div>
                          {msg.subject && <p className="text-white/60 text-[11px] mb-1 italic">{msg.subject}</p>}
                          <p className="text-white text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                          {msg.video_url && (
                            <a
                              href={msg.video_url.startsWith('/api') ? `${API}${msg.video_url}` : msg.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-primary text-xs hover:underline mt-2"
                            >
                              <Video className="h-3 w-3" /> Video attached
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={threadEndRef} />
              </div>

              <div className="p-4 border-t border-white/[0.06]">
                <div className="flex items-end gap-2">
                  <Textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder={`Reply to ${selectedConversation.user_name}…`}
                    className="bg-black/60 border-white/10 focus:border-primary/60 text-white resize-none min-h-[60px] rounded-2xl"
                    data-testid="admin-reply-input"
                  />
                  <button
                    onClick={sendReply}
                    disabled={sendingReply || !replyContent.trim()}
                    className="h-11 w-11 rounded-full flex items-center justify-center bg-gradient-to-br from-yellow-300 to-primary text-black shadow-[0_4px_14px_rgba(250,204,21,0.35)] hover:shadow-[0_6px_20px_rgba(250,204,21,0.5)] disabled:opacity-50 transition-all flex-shrink-0"
                    data-testid="admin-send-reply-btn"
                    aria-label="Send reply"
                  >
                    {sendingReply ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" strokeWidth={2.2} />}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative h-full min-h-[400px] flex items-center justify-center p-8">
              <div className="text-center">
                <div className="h-16 w-16 rounded-full bg-primary/10 border border-primary/25 flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="h-7 w-7 text-primary" />
                </div>
                <p className="font-heading text-white uppercase tracking-wider text-sm">Select a conversation</p>
                <p className="text-muted-foreground text-xs mt-1">Choose an athlete from the list to view messages.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};
